import { useFlyOutProductsBasedOnSelectedCloud } from './useFlyOutProductsBasedOnSelectedCloud'
import { getIsCustomPricing } from '../../../admin/components/ResaleAuthorizationsList/helpers'
import { arrayFieldsForValidations } from '../../../modules/resaleAuthorization/utils/getPricesArray'
import type { ResaleAuthorizationWithErrors } from '../../../modules/resaleAuthorization/types'
import { useSelector } from '../../../store'
import { PlanType } from '@labrav/react-components'
import { useEffect, useState } from 'react'
import { isEmpty, isNil } from 'lodash'
import {
  checkEmptyRows,
  isValidOffersArrayField,
  validateMetaData,
  isErrorInOfferArrayFields,
} from './privateOfferSentDisabled'
import {
  AgreementDurationType,
  ContractDurationType,
  DiscountType,
  ResaleAuthorizationsResellerAgreements,
} from '../../../admin/modules/resaleAuthorizations/reducer'
import {
  Dimension,
  EulaType,
  PriceTypes,
} from '../../../modules/privateOffer/reducer'
import { isNonEmptyNumber } from '@labrav/common'

const singleValueFields = [
  'companyName',
  'agreementName',
  'emailCustomMessage',
  'buyerAwsAccountId',
  'percentageDiscount',
  'resellerAwsAccountId',
  'labraPlatformResellerId',
  'contractDurationInMonthsForEndCustomer',
]
export const useResaleAuthorizationSendAndDraftDisabled = (
  productId: string,
  eulaFiles: File[],
  licenseFiles: File[]
) => {
  const { errors, ...remainingData } =
    useSelector<ResaleAuthorizationWithErrors>(
      state =>
        state.resaleAuthorizationsInCreation
          .currentResaleAuthorizationInCreation[productId] || {}
    )

  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const planType =
    products.find(product => product.productId === productId)?.productType ||
    PlanType.SAAS_CONTRACT

  const resaleAuthorization =
    remainingData as ResaleAuthorizationsResellerAgreements
  const pricesArray = resaleAuthorization?.pricesArray
  const [sendDisabled, setSendDisabled] = useState<boolean>(true)
  const [draftDisabled, setDraftDisabled] = useState<boolean>(true)
  useEffect(() => {
    if (!isEmpty(resaleAuthorization)) {
      const emptyValueFieldsToValidate = [...arrayFieldsForValidations]
      const isCustomPricing = getIsCustomPricing(
        resaleAuthorization?.discountType,
        resaleAuthorization?.contractDurationType
      )
      const flexiblePaymentTerms =
        resaleAuthorization.discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS
      const updatedPricesArray = (
        isCustomPricing ? ['price'] : resaleAuthorization?.pricesArray || []
      ) as [keyof PriceTypes]
      const validateReseller = isValidOffersArrayField({
        key: 'resellersInfo',
        data: resaleAuthorization?.resellersInfo as unknown as Array<
          Record<string, unknown>
        >,
        errors,
        flexiblePaymentTerms,
        planType,
        isTypeCPPO: true,
        pricesArray: updatedPricesArray,
      })
      const metaDataIndex = arrayFieldsForValidations.indexOf('metaData')
      emptyValueFieldsToValidate.splice(metaDataIndex, 1)
      const isErrorInResaleAuthorizationArrayFields = isErrorInOfferArrayFields(
        {
          emptyValueFieldsToValidate,
          offerObject: resaleAuthorization as unknown as Record<
            string,
            unknown
          >,
          errors,
          flexiblePaymentTerms,
          planType,
          isTypeCPPO: true,
          pricesArray: updatedPricesArray,
        }
      )
      const fieldValueCheck = [
        'companyName',
        'agreementName',
        'emailCustomMessage',
      ].some(field =>
        isEmpty(
          (resaleAuthorization as unknown as Record<string, unknown>)[field]
        )
      )

      const singleFieldErrorCheck = singleValueFields.some(
        field => !isEmpty(errors[field])
      )
      const emptyRowCheck = checkEmptyRows(
        resaleAuthorization?.installmentInfo,
        resaleAuthorization?.agreementRecipients,
        resaleAuthorization?.discountType ===
          DiscountType.FLEXIBLE_PAYMENT_TERMS,
        resaleAuthorization?.dimensions as Dimension[],
        resaleAuthorization?.discountType === DiscountType.DISCOUNT
      )
      const renewalCheck = !!(
        resaleAuthorization?.renewal &&
        isNil(resaleAuthorization?.renewalChannel)
      )
      const isBuyerAccountIdMandatory = !!(
        resaleAuthorization?.renewal &&
        isEmpty(resaleAuthorization?.buyerAwsAccountId)
      )
      const discountTypeCheck =
        isEmpty(resaleAuthorization?.discountType) ||
        (resaleAuthorization?.discountType !== DiscountType.DISCOUNT &&
          planType === PlanType.SAAS_CONTRACT &&
          isEmpty(resaleAuthorization?.contractDurationType))
      const discountValidation =
        resaleAuthorization?.discountType === DiscountType.DISCOUNT &&
        (!isNonEmptyNumber(resaleAuthorization?.percentageDiscount) ||
          !isEmpty(errors?.percentageDiscount))
      const durationMonthValidation =
        !isNonEmptyNumber(
          resaleAuthorization?.contractDurationInMonthsForEndCustomer
        ) || !isEmpty(errors?.contractDurationInMonthsForEndCustomer)
      const contractDurationValidationForIndividual =
        resaleAuthorization?.discountType === DiscountType.INDIVIDUAL_PRICING &&
        resaleAuthorization?.contractDurationType ===
          ContractDurationType.CUSTOM &&
        durationMonthValidation
      const contractDurationValidationForFlexible =
        resaleAuthorization?.discountType ===
          DiscountType.FLEXIBLE_PAYMENT_TERMS && durationMonthValidation
      const eulaFilesEmptyCheck =
        resaleAuthorization?.endCustomerEulaType === EulaType.CUSTOM &&
        isEmpty(eulaFiles) &&
        (isEmpty(resaleAuthorization.endCustomerEulaFiles) ||
          resaleAuthorization.endCustomerEulaFiles === null)
      const agreementFilesEmptyCheck =
        resaleAuthorization?.resellerLicenseAgreementType === EulaType.CUSTOM &&
        isEmpty(licenseFiles) &&
        (isEmpty(resaleAuthorization.resellerLicenseAgreementFiles) ||
          resaleAuthorization.resellerLicenseAgreementFiles === null)
      const agreementDurationCheck =
        resaleAuthorization?.agreementDurationType !==
          AgreementDurationType.NO_SET_TIME &&
        isEmpty(resaleAuthorization?.agreementExpirationDate)
      const metaDataCheck =
        !isEmpty(resaleAuthorization?.metaData) &&
        validateMetaData(
          resaleAuthorization?.metaData as unknown as Array<
            Record<string, unknown>
          >,
          errors.metaData as Array<Record<string, unknown>>
        )
      setSendDisabled(
        isErrorInResaleAuthorizationArrayFields ||
          fieldValueCheck ||
          singleFieldErrorCheck ||
          contractDurationValidationForIndividual ||
          contractDurationValidationForFlexible ||
          discountValidation ||
          validateReseller ||
          discountTypeCheck ||
          eulaFilesEmptyCheck ||
          agreementFilesEmptyCheck ||
          agreementDurationCheck ||
          metaDataCheck ||
          emptyRowCheck ||
          renewalCheck ||
          isBuyerAccountIdMandatory
      )
      setDraftDisabled(
        isErrorInResaleAuthorizationArrayFields || singleFieldErrorCheck
      )
    }
  }, [planType, resaleAuthorization, pricesArray?.length, errors])
  return { sendDisabled, draftDisabled }
}
